/**
 * 長野県の料理12品。レベルが上がるほど大きく、器つきの汁物・鍋物になる。
 *
 * 選定のときに迷った点を残しておく。
 * - 「笹ずし」は新潟の上越にも伝わるが、新潟側は笹団子を採って笹寿司を外してあるので、
 *   飯山の品として長野に置いた。
 * - 「鯉こく」は山形の「鯉のうま煮」とは別物。長野は筒切りにして味噌で3時間前後煮込む汁物である。
 * - 「戸隠そば」の盛り方は、公的な出典には「小分けにした盛り付けは『ぼっち盛り』と呼ばれている」
 *   とだけ書かれていて、器の記述がない。新潟のへぎ（四角い剥ぎ板）、島根の丸い重箱とはそこで区別できる。
 *   ぼっちの数は出典にないので、数は書かない。
 * - 「天寄せ」は祝儀のときは紅、不祝儀のときは緑と色で使い分けると出典が書いている。紅を採った。
 * - 「いなごの佃煮」は虫料理なので入れていない。
 * - 「おしぼりうどん」はうどんが他県に多いので入れていない。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：のたもち・やしょうま・天寄せ・すんき漬け・ほう葉巻（店で食べられる品に差し替え）
 * - 松本山賊焼：発祥（松本説・塩尻説）は割れていて公式で決められないので書かない
 * - 松本山賊焼：松本山賊焼応援団の『70年余り続く秘伝のタレ』は特定の店の話にとれるので書かない
 * - 松本山賊焼：松本山賊焼五ヶ条は条文をページから読み取れなかったので書かない
 * - 伊那ローメン：考案した店・考案の年は伊那市ページに記載がなく確かめられないので書かない
 * - 伊那ローメン：店の数（伊那市ページでは約90軒）は変わる情報なので「多くの店」とした
 * - 伊那ローメン：食べるときにソースや酢を足す習わしは、公式で確かめていないので書かない
 * - 駒ヶ根ソースかつ丼：発祥の店・発祥の年は公式で確かめられないので書かない
 * - 駒ヶ根ソースかつ丼：提供店の名前は書かない
 * - 馬刺し：伊那市ページの『信州で馬肉を食べる習慣は伊那がはじまり』は「最古」型の主張で、松本側の資料（Go! NAGANO）と食い違いうるので書かない
 * - 馬刺し：『ばくろう』（馬の行商人）とさくら鍋の伝来説は説が一つに定まっていないので書かない
 * - 信州プレミアム牛：オレイン酸の数値基準（58%以上かつBMS5以上 など）は図鑑には細かすぎるので書かない（出典にはある）
 * - 信州プレミアム牛：品名を「信州牛のすき焼き」に変える案もあったが、店のページでの確認が通ったので、今回のリリースはこの名前のままにする（2026-10-02 相島さん決定）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { naganoSceneryTour } from './naganoScenes';
import { NAGANO_FOOD_HULLS } from './naganoFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'konetsuke',
    level: 1,
    name: 'こねつけ',
    shortName: 'こねつけ',
    image: '/assets/foods/nagano/01_konetsuke.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: 'ご飯と小麦粉を混ぜてこね、棒状にのばして1.5センチ幅に輪切りにし、フライパンで両面を焼いて、煮立てたたれで味をつける。米が不作のときに満腹感を味わうために小麦粉を混ぜて焼いた。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/kone_tsuke_nagano.html',
    icon: { label: 'こねつけ', shape: 'mochi', baseColor: '#e0cfa8', accentColor: '#7a4a20' },
    reward: {
      unlockScore: 200,
      area: '上田市',
      headline: '真田幸村が出陣前に食べた',
      tip: '戦国武将の真田幸村が出陣前の栄養補給に食したとされ、大坂夏の陣での兄との別れの盃とともに食したという逸話が伝わる。いまはおやつとして食べることが多い。',
      links: searchLinks('上田市', 'こねつけ'),
    },
  },
  {
    id: 'oyaki',
    level: 2,
    name: 'おやき',
    shortName: 'おやき',
    image: '/assets/foods/nagano/02_oyaki.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '小麦粉や蕎麦粉などを水または湯で溶いて練り、薄くのばした皮に、あんや野菜など旬のものを包んで焼いたもの。生地40グラムを丸くのばして具を包み、フライパンで両面を焼いた後、15分蒸す。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/oyaki_nagano.html',
    icon: { label: 'おやき', shape: 'mochi', baseColor: '#e0cfa0', accentColor: '#4a3320' },
    reward: {
      unlockScore: 500,
      area: '長野市',
      headline: '信州を代表する郷土料理',
      tip: '上水内郡西山地域が発祥とされ、小川村の縄文遺跡からは雑穀粉を焼いた跡が発見されている。昔は囲炉裏で「ほうろく」という鉄製の鍋を使い、灰の中で蒸し焼きにする「灰焼きおやき」が主流だった。',
      links: searchLinks('長野市', 'おやき'),
    },
  },
  {
    id: 'nozawana-zuke',
    level: 3,
    name: '野沢菜漬',
    shortName: '野沢菜漬',
    image: '/assets/foods/nagano/03_nozawana.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '株を少し残して切り落とした野沢菜を、塩と焼酎と昆布と唐辛子で漬け、30〜40キロの重石をかける。漬けて2〜3週間で食べられ、おいしくなるのは1か月後の年末年始ごろ。乳酸発酵が進むとべっ甲色になる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/nozawana_zuke_nagano.html',
    icon: { label: '野沢菜漬', shape: 'roe', baseColor: '#6b5a28', accentColor: '#c9382f' },
    reward: {
      unlockScore: 1000,
      area: '野沢温泉村',
      headline: '根が育たず葉と茎だけ大きく',
      tip: '県の北東にある野沢温泉村の住職が、宝暦6年に京都から天王寺かぶを持ち帰って植えたのが始まりとされる。高冷地の気候により根が育たず葉と茎だけが大きくなり、地名のついた「野沢菜」として広まった。',
      links: searchLinks('野沢温泉村', '野沢菜漬'),
    },
  },
  {
    id: 'matsumoto-sanzokuyaki',
    level: 4,
    name: '松本山賊焼',
    shortName: '松本山賊焼',
    image: '/assets/foods/nagano/04_matsumoto_sanzokuyaki.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: 'すりおろしたにんにくをきかせた醤油だれに鶏のもも肉やむね肉をじっくり漬けこみ、片栗粉をまぶして高温で揚げる。鶏の一枚肉を揚げた料理で、外はカリッと香ばしく、中はジューシーに仕上がる。',
    source:
      'https://visitmatsumoto.com/article/gourmet',
    icon: { label: '山賊焼', shape: 'curry-plate', baseColor: '#b8742e', accentColor: '#f1e4c8' },
    reward: {
      unlockScore: 1800,
      area: '松本市',
      headline: '鶏一枚を丸ごと揚げる',
      tip: '名前は、山賊は物を「とりあげる」＝「鶏揚げる」という語呂合わせから。毎年3月9日は「さん（3）ぞく（9）」にちなむ「松本山賊焼の日」とされる。',
      links: searchLinks('松本市', '山賊焼'),
      official: [{ label: '松本山賊焼応援団（事務局：松本商工会議所）', url: 'https://www.sanzokun.jp/' }],
    },
  },
  {
    id: 'sasa-zushi',
    level: 5,
    name: '笹ずし',
    shortName: '笹ずし',
    image: '/assets/foods/nagano/05_sasa_zushi.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '笹の上に酢飯を楕円形に広げ、中央をへこませて、ぜんまい、たけのこ、しいたけ、くるみなど山の幸をのせ、錦糸卵や紅生姜で彩りを添える。箸などを使わず、笹を下に引きながら直接食べる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/sasa_zushi_nagano.html',
    icon: { label: '笹ずし', shape: 'sashimi-platter', baseColor: '#3f6b2a', accentColor: '#e8c84a' },
    reward: {
      unlockScore: 3000,
      area: '飯山市',
      headline: '謙信寿司とも呼ばれる',
      tip: '天文22年（1553年）から12年にわたって争われた川中島の合戦で、上杉謙信の軍勢にふるまわれたことが始まりという説がある。平成19年に長野県選択無形民俗文化財に指定された。',
      links: searchLinks('飯山市', '笹ずし'),
    },
  },
  {
    id: 'ina-romen',
    level: 6,
    name: '伊那ローメン',
    shortName: '伊那ローメン',
    image: '/assets/foods/nagano/06_ina_romen.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '蒸した固めの中華麺を、羊の肉やキャベツ、キクラゲなどと一緒に独特のスープで煮込む、伊那市で生まれた料理。スープ風と焼きそば風の2種類がある。',
    source:
      'https://www.inacity.jp/kankojoho/taberu_kau/romen.html',
    icon: { label: 'ローメン', shape: 'noodle-bowl', baseColor: '#8a5a32', accentColor: '#7fa548' },
    reward: {
      unlockScore: 4500,
      area: '伊那市ほか上伊那地方',
      headline: '蒸した麺と羊肉を煮込む',
      tip: '上伊那地方の多くの店のメニューに載っている。地元を離れた人が帰省したときに食べる郷土の味として定着している。',
      links: searchLinks('伊那市', 'ローメン'),
      official: [{ label: '伊那市「ローメン」', url: 'https://www.inacity.jp/kankojoho/taberu_kau/romen.html' }],
    },
  },
  {
    id: 'takenoko-jiru',
    level: 7,
    name: 'たけのこ汁',
    shortName: 'たけのこ汁',
    image: '/assets/foods/nagano/07_takenoko_jiru.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '根曲がり竹を使った味噌汁。根曲がり竹は「ちまざさ」という笹の新芽で、初夏のわずかな時期にしか採れない。サバの水煮缶を入れるのが、海のない長野県ならではの食べ方。豆腐と溶き卵も入る。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/takenoko_jiru_nagano.html',
    icon: { label: 'たけのこ汁', shape: 'hotpot', baseColor: '#c9b078', accentColor: '#e8c84a' },
    reward: {
      unlockScore: 6500,
      area: '飯山市',
      headline: '細く曲がった笹の新芽',
      tip: '根曲がり竹は多雪地帯特有で、アクが少なく味が良いとされる。サバの水煮缶は昭和30年代に普及し、海のない長野県では重宝された。味噌汁に油とコクを加える地域特有の食べ方。',
      links: searchLinks('飯山市', 'たけのこ汁'),
    },
  },
  {
    id: 'komagane-sauce-katsudon',
    level: 8,
    name: '駒ヶ根ソースかつ丼',
    shortName: 'ソースかつ丼',
    image: '/assets/foods/nagano/08_komagane_sauce_katsudon.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: 'ごはんにキャベツをのせ、その上に秘伝のソースで味付けした分厚いカツをのせる。駒ヶ根観光協会が、そばと並ぶ駒ヶ根のご当地グルメとして紹介している。',
    source:
      'https://www.kankou-komagane.com/gourmet/',
    icon: { label: 'かつ丼', shape: 'rice-bowl', baseColor: '#7a4320', accentColor: '#8cbf4a' },
    reward: {
      unlockScore: 9000,
      area: '駒ヶ根市',
      headline: 'キャベツの上に厚いカツ',
      links: searchLinks('駒ヶ根市', 'ソースかつ丼'),
      official: [{ label: '駒ヶ根観光協会「駒ヶ根の【食】」', url: 'https://www.kankou-komagane.com/gourmet/' }],
    },
  },
  {
    id: 'togakushi-soba',
    level: 9,
    name: '戸隠そば',
    shortName: '戸隠そば',
    image: '/assets/foods/nagano/09_togakushi_soba.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '一本棒・丸伸ばしという古くから伝わるそば打ち方法で打ち、小分けにして盛る。この盛り付けは「ぼっち盛り」と呼ばれている。「霜下そば」とも呼ばれ、風味が良く、冷たい水でキュッとしめられ、のど越しがよい。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/teuchi_soba_nagano.html',
    icon: { label: '戸隠そば', shape: 'noodle-bowl', baseColor: '#6b5c4a', accentColor: '#3f5c2a' },
    reward: {
      unlockScore: 12000,
      area: '長野市戸隠',
      headline: '小分けにして盛る',
      tip: '県内には「戸隠そば」「開田そば」などそばの特産・名産地が多く点在する。伊那の「高遠そば」は大根汁と焼き味噌を加えたつゆ、奈川の「とうじそば」は鍋つゆで温めて食べる。',
      links: searchLinks('長野市', '戸隠そば'),
    },
  },
  {
    id: 'koikoku',
    level: 10,
    name: '鯉こく',
    shortName: '鯉こく',
    image: '/assets/foods/nagano/10_koikoku.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: 'コイを大胆に筒切りにして味噌で煮る。味噌とごはんをすり鉢で混ぜてから加え、3時間前後煮込む。仕上げにねぎ、豆腐、凍み豆腐、粉山椒、ゆでた芹を添える。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/koikoku_nagano.html',
    icon: { label: '鯉こく', shape: 'pot', baseColor: '#8a6a3a', accentColor: '#4c7a2a' },
    reward: {
      unlockScore: 16000,
      area: '佐久市',
      headline: '1月6日は佐久鯉の誕生日',
      tip: '佐久鯉の養殖は文政8年（1825年）に岩村田藩主が淀鯉を導入したことに始まる。1746年1月6日に篠澤佐五右衛門が伊勢神宮の神官を招いて鯉こくを振る舞ったことが正月に食べる習慣の由来とされ、1月6日は「佐久鯉の誕生日」として記念日に制定された。',
      links: searchLinks('佐久市', '鯉こく'),
    },
  },
  {
    id: 'nagano-basashi',
    level: 11,
    name: '馬刺し',
    shortName: '馬刺し',
    image: '/assets/foods/nagano/11_nagano_basashi.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '馬肉の刺身を、おろししょうがと醤油で食べる。とろっとした舌触りが特徴。伊那では、馬のモツを醤油や味噌でじっくり煮込んだ「おたぐり」も馬肉料理として居酒屋などで出される。',
    source:
      'https://www.inacity.jp/kankojoho/taberu_kau/banikuryori.html',
    icon: { label: '馬刺し', shape: 'sashimi-platter', baseColor: '#9e2b2b', accentColor: '#f0e2c0' },
    reward: {
      unlockScore: 22000,
      area: '伊那市／松本市ほか中南信',
      headline: 'しょうが醤油で食べる馬肉',
      tip: '明治時代、信濃と三河を結ぶ三州街道では馬で荷を運ぶことが盛んだった。明治後期に年老いた農耕馬を食べるようになったのが始まりとされ、馬肉は「さくら肉」とも呼ばれる。',
      links: searchLinks('伊那市', '馬刺し'),
      official: [{ label: 'Go! NAGANO 長野県公式観光サイト「長野県に根付いた馬肉文化を追う～松本馬刺し編～」', url: 'https://www.go-nagano.net/food-and-drink/id22158?hsLang=ja-jp' }, { label: '松本市観光サイト「松本で食べたいご当地グルメ」', url: 'https://visitmatsumoto.com/article/gourmet' }],
    },
  },
  {
    id: 'shinshu-premium-beef',
    level: 12,
    name: '信州プレミアム牛',
    shortName: '信州プレミアム牛',
    image: '/assets/foods/nagano/12_shinshu_premium_beef.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '一つのブランド牛ではなく、長野県が定めた基準を満たす牛肉の総称。県の「信州あんしん農産物」生産認定農場で育った牛のうち、香りや口どけの指標となるオレイン酸の割合と、脂肪交雑（BMS）の組み合わせで認定される。',
    source:
      'https://www.go-nagano.net/food-and-drink/id18667?hsLang=ja-jp',
    icon: { label: '信州牛', shape: 'curry-plate', baseColor: '#8f3a2c', accentColor: '#8fbf5a' },
    reward: {
      unlockScore: 30000,
      area: '長野県内各地',
      headline: '県の基準で選ばれる牛肉',
      tip: 'ステーキにはもも肉を使うと加熱してもやわらかい。長野県産のわさびや岩塩でシンプルに味わうのがすすめられている。すき焼きにはリブロースがよいとされる。',
      links: searchLinks('長野県', '信州プレミアム牛肉'),
      official: [{ label: 'Go! NAGANO「信州プレミアム牛肉」', url: 'https://www.go-nagano.net/food-and-drink/id18667?hsLang=ja-jp' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const naganoFoodStage: StageDefinition = {
  regionId: 'nagano',
  regionName: '長野県',
  mode: 'food',
  title: '長野めしマージ',
  theme: {
    primaryColor: '#3a5c3a',
    secondaryColor: '#ece6d6',
    accentColor: '#a83a3a',
    backgroundColor: '#f9f5ec',
  },
  /** 背景ツアー「長野めぐり」。軸は季節。4月の高遠から始めて、2月の地獄谷で終わる。北信から木曽、中央アルプスまでを通る。 */
  scenery: {
    tour: naganoSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, NAGANO_FOOD_HULLS),
};
